import type { ProjectState, ToolType } from '@/lib/types';

// ── L'etat de l'editeur, lu par le support au moment d'ecrire ──
// Un registre d'une seule fonction : l'editeur monte y inscrit la sienne et la retire au demontage.
// Hors de l'editeur, le registre est vide.

export type ViewMode = '2d' | '3d' | 'visit' | 'section';

export interface EditorProbe {
  /** Le projet affiche, modifications non enregistrees comprises */
  project: ProjectState;
  viewMode?: ViewMode;
  activeFloorId?: string;
  activeTool?: ToolType;
  selection?: { type: string; id: string; label: string };
  /** La camera de la vue 3D, si elle est montee */
  camera?: { position: [number, number, number]; target: [number, number, number] };
  /** Les changements du projet depuis son ouverture, et ce nombre au dernier enregistrement reussi */
  revision?: number;
  savedRevision?: number;
  /** L'issue du dernier enregistrement de cette page */
  lastSave?: { at: string; ok: boolean };
  /**
   * La vue 3D, rendue a l'instant en image, avec sa place a l'ecran : le secours de la capture la colle sur la page
   * (un canvas WebGL sans preserveDrawingBuffer sortirait noir). Absente si la vue 3D n'est pas montee.
   */
  renderToDataUrl?: () => View3DImage;
}

export interface View3DImage {
  dataUrl: string;
  rect: { left: number; top: number; width: number; height: number };
}

let probe: (() => EditorProbe) | null = null;
let view3d: (() => View3DImage) | null = null;

/** Inscrit la fonction de l'editeur ; renvoie de quoi la retirer (sans toucher a celle d'un autre editeur) */
export function registerEditorProbe(fn: () => EditorProbe): () => void {
  probe = fn;
  return () => {
    if (probe === fn) probe = null;
  };
}

/** La vue 3D montee inscrit son rendu en image (independant de l'editeur, qui la contient) */
export function registerView3DRenderer(fn: () => View3DImage): () => void {
  view3d = fn;
  return () => {
    if (view3d === fn) view3d = null;
  };
}

export function readEditorProbe(): EditorProbe | null {
  if (!probe) return null;
  const state = probe();
  return view3d ? { ...state, renderToDataUrl: view3d } : state;
}
